import { createContext, useContext, useEffect, useState } from "react";

const translations = {
    en: {
        nav: { join: "Join Guestlist", member: "Members", admin: "Admin" },
        landing: {
            eyebrow: "John Scotts · Kungsträdgården",
            hero1: "ScottsClub",
            hero2: "Exclusive nights. Exclusive members.",
            hero_sub: "A private nightlife community for the city's most curated crowd. Membership by application only.",
            cta_primary: "Apply for Membership",
            cta_secondary: "I'm already a member",
            section_about_eyebrow: "The Club",
            section_about_title: "An invitation-only circle in the heart of Stockholm.",
            section_about_body: "ScottsClub is the private membership layer of John Scotts Kungsträdgården. Skip the line, claim your table, and unlock evenings curated only for the people who shape the city's nightlife.",
            section_perks_eyebrow: "Membership",
            section_perks_title: "What's inside the velvet rope.",
            perk1_title: "Guestlist access",
            perk1_body: "Priority entry on Fridays, Saturdays and theme nights — for you and a guest.",
            perk2_title: "Private events",
            perk2_body: "Invitations to closed-door tastings, after-hours sets and seasonal salons.",
            perk3_title: "Personal member QR",
            perk3_body: "One scan at the door. The team recognises you the moment you arrive.",
            section_how_eyebrow: "The Process",
            section_how_title: "Three steps. One night.",
            step1: "Apply with a photo and your Instagram. We review every application personally.",
            step2: "We respond within 48 hours. Decisions are final and discreet.",
            step3: "Approved members receive a permanent digital pass for the door.",
            footer_note: "John Scotts Kungsträdgården · Hamngatan 19 · Stockholm",
            footer_copy: "© ScottsClub. A private members community.",
        },
        join: {
            eyebrow: "Membership Application",
            title: "Apply to ScottsClub",
            subtitle: "30 seconds. A photo, your name and number — the door takes it from there.",
            label_first_name: "First name",
            ph_first_name: "Anna",
            label_last_name: "Last name",
            ph_last_name: "Lindström",
            label_phone: "Phone Number",
            ph_phone: "+46 70 123 45 67",
            label_note: "Optional note for the host",
            ph_note: "Tell us briefly why ScottsClub is for you (optional)",
            label_photo: "Selfie / Profile Photo",
            photo_help: "Required. A clear photo of your face — this is how the door recognises you.",
            photo_drop: "Tap to upload your photo",
            photo_replace: "Replace photo",
            ig_step_eyebrow: "Community Verification",
            ig_step_title: "Follow us on Instagram",
            ig_step_sub: "Membership requires community verification via Instagram.",
            ig_open_button: "Open Instagram & Follow",
            ig_opened_again: "Open again",
            ig_confirm_label: "I have followed ScottsClub on Instagram",
            ig_confirm_locked: "Open Instagram first, then confirm here.",
            age_label: "I confirm I am 18 years or older.",
            sms_label: "I agree to receive SMS from ScottsClub with new offers, events and members-only invitations.",
            review_label: "I understand my application will be manually reviewed.",
            submit: "Apply for Membership",
            submitting: "Sending application…",
            error_required: "Please complete every required field.",
        },
        success: {
            eyebrow: "Application received",
            title: "Welcome to the waitlist.",
            body: "Your application is now under review by ScottsClub admins. We'll be in touch within 48 hours.",
            tip: "Approved members will receive a personal QR pass. Keep an eye on your phone — and your Instagram DMs.",
            cta: "Back to the entrance",
        },
        member: {
            eyebrow: "Member Access",
            title: "Welcome back.",
            subtitle: "Enter the phone number you applied with to access your pass.",
            ph_phone: "+46 70 123 45 67",
            cta: "Open my pass",
            looking: "Looking you up…",
            pending: "Your application is still under review. Sit tight — we won't be long.",
            rejected: "Unfortunately, your application was not approved at this time.",
            not_found: "We couldn't find an application for that number. Try the application page.",
            denied: "Access denied.",
        },
        vip: {
            eyebrow: "Digital Pass",
            tagline: "Show this at the door.",
            status_approved: "Approved Member",
            status_vip: "VIP Member",
            check_ins: "Visits",
            joined: "Member since",
            signout: "Sign out",
            instagram: "Instagram",
        },
        admin: {
            eyebrow: "Staff Only",
            login_title: "ScottsClub Admin",
            login_sub: "Sign in to manage applications, members and events.",
            email: "Email",
            password: "Password",
            login_cta: "Sign in",
            tabs: { pending: "Pending", approved: "Members", rejected: "Rejected", blacklist: "Blacklist", all: "All" },
            stats: {
                title: "Tonight's room",
                pending: "Pending Review",
                approved: "Approved Members",
                vip: "VIP Members",
                rejected: "Rejected",
                blacklisted: "Blacklisted",
                last_week: "New (7 days)",
                top: "Most active members",
                no_top: "No check-ins yet.",
            },
            search_ph: "Search by name, IG, phone…",
            no_results: "No applications match the filter.",
            cols: { name: "Member", ig: "Instagram", phone: "Phone", applied: "Applied", status: "Status", actions: "Actions" },
            actions: {
                approve: "Approve",
                reject: "Reject",
                vip: "Tag VIP",
                unvip: "Remove VIP",
                ban: "Blacklist",
                restore: "Restore",
                check_in: "Check in",
                save_notes: "Save notes",
                delete: "Delete forever",
            },
            detail_title: "Application detail",
            notes_label: "Internal notes",
            notes_ph: "Notes about this member (visible to admins only)",
            signed_in_as: "Signed in as",
            signout: "Sign out",
            confirm_delete: "Permanently delete this application? This cannot be undone.",
        },
        common: {
            close: "Close",
            cancel: "Cancel",
            saving: "Saving…",
            saved: "Saved",
        },
        scan: {
            eyebrow: "Door Scanner",
            title: "Scan member pass",
            subtitle: "Point the camera at the member's QR code. The result will appear instantly.",
            start: "Start camera",
            stop: "Stop camera",
            manual: "Enter member ID manually",
            verify: "Verify",
            check_in_cta: "Check in member",
            blacklist_warn: "BLACKLISTED — do not admit.",
            inactive_warn: "Not an active member.",
            scan_again: "Scan another",
            ok_admit: "OK to admit",
            checked_in: "Checked in",
            error: "Could not read that QR.",
        },
        posters: {
            eyebrow: "QR Posters",
            title: "Print-ready QR posters",
            subtitle: "Print these on A4. Tape one at the door for guests, keep the staff one in the office.",
            guest_title: "Apply to ScottsClub",
            guest_sub: "Scan with your phone camera",
            staff_title: "Staff entrance",
            staff_sub: "ScottsClub admin · staff only",
            print: "Print",
        },
        staff: {
            eyebrow: "Door Staff",
            login_title: "Door Staff Sign-in",
            login_sub: "Enter your personal door PIN.",
            pin_label: "PIN",
            login_cta: "Sign in",
            welcome: "On shift",
            signout: "End shift",
            tabs: { scan: "Scan", members: "Members" },
            members_search_ph: "Search approved members…",
            no_members: "No members match.",
            check_in: "Check in",
            visits: "visits",
        },
        door_admin: {
            title: "Door Staff PINs",
            sub: "Create one PIN per person so check-ins are attributed.",
            add: "Add staff",
            name: "Name",
            pin: "PIN (4–12 digits)",
            create: "Create",
            empty: "No door staff yet. Add your first to enable PIN sign-in.",
            last_used: "Last used",
            never: "Never",
            rotate: "Rotate PIN",
            new_pin: "New PIN",
            deactivate: "Deactivate",
            activate: "Activate",
            remove: "Remove",
            confirm_remove: "Remove this PIN? They will lose access immediately.",
            active: "Active",
            inactive: "Inactive",
        },
        occ: {
            inside_now: "Inside Now",
            entries_tonight: "Entries Tonight",
            who_inside: "Currently inside",
            no_one: "Nobody is inside right now.",
            check_out: "Check out",
            inside_pill: "Inside",
            duplicate: "Already scanned a moment ago — not double-counted.",
            already_inside: "Already inside · not re-counted.",
            checked_out: "Checked out",
        },
        profile: {
            edit: "Edit profile",
            first_name: "First name",
            last_name: "Last name",
            change_photo: "Change photo",
            save: "Save changes",
            saved: "Profile updated",
            uploading: "Uploading…",
        },
    },
    sv: {
        nav: { join: "Ansök", member: "Medlem", admin: "Admin" },
        landing: {
            eyebrow: "John Scotts · Kungsträdgården",
            hero1: "ScottsClub",
            hero2: "Exklusiva kvällar. Exklusiva medlemmar.",
            hero_sub: "En privat nattlivsklubb för stadens mest kurerade gäster. Medlemskap endast via ansökan.",
            cta_primary: "Ansök om medlemskap",
            cta_secondary: "Jag är redan medlem",
            section_about_eyebrow: "Klubben",
            section_about_title: "En sluten krets mitt i Stockholm.",
            section_about_body: "ScottsClub är det privata medlemskapet hos John Scotts Kungsträdgården. Slipp kön, säkra ditt bord och få tillgång till kvällar som bara delas med dem som formar stadens nattliv.",
            section_perks_eyebrow: "Medlemskapet",
            section_perks_title: "Det här ingår innanför sammetslinan.",
            perk1_title: "Gästlista",
            perk1_body: "Prioriterat insläpp på fredagar, lördagar och temakvällar — för dig och en gäst.",
            perk2_title: "Slutna events",
            perk2_body: "Inbjudningar till exklusiva provningar, after-hours och säsongssalonger.",
            perk3_title: "Personlig QR-kod",
            perk3_body: "En scanning vid dörren. Teamet känner igen dig direkt när du kommer.",
            section_how_eyebrow: "Processen",
            section_how_title: "Tre steg. En kväll.",
            step1: "Ansök med en bild och ditt Instagram. Vi läser varje ansökan personligen.",
            step2: "Vi återkommer inom 48 timmar. Beslutet är slutgiltigt och diskret.",
            step3: "Godkända medlemmar får ett permanent digitalt pass för dörren.",
            footer_note: "John Scotts Kungsträdgården · Hamngatan 19 · Stockholm",
            footer_copy: "© ScottsClub. En privat medlemsgemenskap.",
        },
        join: {
            eyebrow: "Ansökan om medlemskap",
            title: "Ansök till ScottsClub",
            subtitle: "30 sekunder. En bild, ditt namn och nummer — resten sköter dörren.",
            label_first_name: "Förnamn",
            ph_first_name: "Anna",
            label_last_name: "Efternamn",
            ph_last_name: "Lindström",
            label_phone: "Mobilnummer",
            ph_phone: "+46 70 123 45 67",
            label_note: "Frivillig hälsning till värden",
            ph_note: "Berätta kort varför ScottsClub är något för dig (frivilligt)",
            label_photo: "Selfie / Profilbild",
            photo_help: "Obligatoriskt. En tydlig bild av ditt ansikte — så känner dörren igen dig.",
            photo_drop: "Tryck för att ladda upp din bild",
            photo_replace: "Byt bild",
            ig_step_eyebrow: "Communityverifiering",
            ig_step_title: "Följ oss på Instagram",
            ig_step_sub: "Medlemskap kräver communityverifiering via Instagram.",
            ig_open_button: "Öppna Instagram & följ",
            ig_opened_again: "Öppna igen",
            ig_confirm_label: "Jag följer ScottsClub på Instagram",
            ig_confirm_locked: "Öppna Instagram först, bekräfta sedan här.",
            age_label: "Jag intygar att jag är 18 år eller äldre.",
            sms_label: "Jag godkänner att få SMS från ScottsClub med nya erbjudanden, event och medlemsinbjudningar.",
            review_label: "Jag förstår att min ansökan granskas manuellt.",
            submit: "Ansök om medlemskap",
            submitting: "Skickar ansökan…",
            error_required: "Vänligen fyll i alla obligatoriska fält.",
        },
        success: {
            eyebrow: "Ansökan mottagen",
            title: "Välkommen till väntelistan.",
            body: "Din ansökan granskas nu av ScottsClubs admins. Vi hör av oss inom 48 timmar.",
            tip: "Godkända medlemmar får ett personligt QR-pass. Håll koll på din telefon — och dina Instagram-DM.",
            cta: "Tillbaka till entrén",
        },
        member: {
            eyebrow: "Medlemsåtkomst",
            title: "Välkommen tillbaka.",
            subtitle: "Ange numret du ansökte med för att öppna ditt pass.",
            ph_phone: "+46 70 123 45 67",
            cta: "Öppna mitt pass",
            looking: "Letar upp dig…",
            pending: "Din ansökan granskas fortfarande. Vi är snart klara.",
            rejected: "Tyvärr blev din ansökan inte godkänd den här gången.",
            not_found: "Vi hittade ingen ansökan på det numret. Pröva ansökningssidan.",
            denied: "Åtkomst nekad.",
        },
        vip: {
            eyebrow: "Digitalt pass",
            tagline: "Visa detta vid dörren.",
            status_approved: "Godkänd medlem",
            status_vip: "VIP-medlem",
            check_ins: "Besök",
            joined: "Medlem sedan",
            signout: "Logga ut",
            instagram: "Instagram",
        },
        admin: {
            eyebrow: "Endast personal",
            login_title: "ScottsClub Admin",
            login_sub: "Logga in för att hantera ansökningar, medlemmar och events.",
            email: "E-post",
            password: "Lösenord",
            login_cta: "Logga in",
            tabs: { pending: "Väntar", approved: "Medlemmar", rejected: "Avslagna", blacklist: "Svartlista", all: "Alla" },
            stats: {
                title: "Kvällens rum",
                pending: "Väntar på granskning",
                approved: "Godkända medlemmar",
                vip: "VIP-medlemmar",
                rejected: "Avslagna",
                blacklisted: "Svartlistade",
                last_week: "Nya (7 dagar)",
                top: "Mest aktiva medlemmar",
                no_top: "Inga incheckningar ännu.",
            },
            search_ph: "Sök namn, IG, telefon…",
            no_results: "Inga ansökningar matchar.",
            cols: { name: "Medlem", ig: "Instagram", phone: "Telefon", applied: "Ansökt", status: "Status", actions: "Åtgärder" },
            actions: {
                approve: "Godkänn",
                reject: "Avslå",
                vip: "Märk som VIP",
                unvip: "Ta bort VIP",
                ban: "Svartlista",
                restore: "Återställ",
                check_in: "Checka in",
                save_notes: "Spara anteckningar",
                delete: "Ta bort permanent",
            },
            detail_title: "Ansökningsdetaljer",
            notes_label: "Interna anteckningar",
            notes_ph: "Anteckningar om medlemmen (syns endast för admins)",
            signed_in_as: "Inloggad som",
            signout: "Logga ut",
            confirm_delete: "Ta bort denna ansökan permanent? Detta kan inte ångras.",
        },
        common: {
            close: "Stäng",
            cancel: "Avbryt",
            saving: "Sparar…",
            saved: "Sparat",
        },
        scan: {
            eyebrow: "Dörrscanner",
            title: "Skanna medlemspass",
            subtitle: "Rikta kameran mot medlemmens QR-kod. Resultatet visas direkt.",
            start: "Starta kamera",
            stop: "Stoppa kamera",
            manual: "Ange medlems-ID manuellt",
            verify: "Verifiera",
            check_in_cta: "Checka in medlem",
            blacklist_warn: "SVARTLISTAD — släpp inte in.",
            inactive_warn: "Ingen aktiv medlem.",
            scan_again: "Skanna en till",
            ok_admit: "OK att släppa in",
            checked_in: "Incheckad",
            error: "Kunde inte läsa QR-koden.",
        },
        posters: {
            eyebrow: "QR-affischer",
            title: "Tryckklara QR-affischer",
            subtitle: "Skriv ut på A4. Sätt upp en vid entrén för gäster och behåll personalaffischen på kontoret.",
            guest_title: "Ansök till ScottsClub",
            guest_sub: "Skanna med din mobilkamera",
            staff_title: "Personalingång",
            staff_sub: "ScottsClub admin · endast personal",
            print: "Skriv ut",
        },
        staff: {
            eyebrow: "Dörrpersonal",
            login_title: "Dörrpersonal-inloggning",
            login_sub: "Ange din personliga dörr-PIN.",
            pin_label: "PIN",
            login_cta: "Logga in",
            welcome: "På pass",
            signout: "Avsluta pass",
            tabs: { scan: "Skanna", members: "Medlemmar" },
            members_search_ph: "Sök godkända medlemmar…",
            no_members: "Inga medlemmar matchar.",
            check_in: "Checka in",
            visits: "besök",
        },
        door_admin: {
            title: "PIN-koder för dörrpersonal",
            sub: "Skapa en PIN per person så att incheckningar kopplas till någon.",
            add: "Lägg till personal",
            name: "Namn",
            pin: "PIN (4–12 siffror)",
            create: "Skapa",
            empty: "Ingen dörrpersonal ännu. Lägg till första för att aktivera PIN-inloggning.",
            last_used: "Senast använd",
            never: "Aldrig",
            rotate: "Byt PIN",
            new_pin: "Ny PIN",
            deactivate: "Inaktivera",
            activate: "Aktivera",
            remove: "Ta bort",
            confirm_remove: "Ta bort denna PIN? Personen tappar åtkomst direkt.",
            active: "Aktiv",
            inactive: "Inaktiv",
        },
        occ: {
            inside_now: "Inne just nu",
            entries_tonight: "Insläpp i kväll",
            who_inside: "Inne just nu",
            no_one: "Ingen är inne just nu.",
            check_out: "Checka ut",
            inside_pill: "Inne",
            duplicate: "Skannades nyss — räknas inte dubbelt.",
            already_inside: "Redan inne · räknas inte igen.",
            checked_out: "Utcheckad",
        },
        profile: {
            edit: "Redigera profil",
            first_name: "Förnamn",
            last_name: "Efternamn",
            change_photo: "Byt bild",
            save: "Spara ändringar",
            saved: "Profilen uppdaterad",
            uploading: "Laddar upp…",
        },
    },
};

const LanguageContext = createContext({ lang: "en", setLang: () => {}, t: (k) => k });

export function LanguageProvider({ children }) {
    const [lang, setLang] = useState(() => localStorage.getItem("sc_lang") || "sv");
    useEffect(() => { localStorage.setItem("sc_lang", lang); }, [lang]);

    const t = (path) => {
        const parts = path.split(".");
        let cur = translations[lang];
        for (const p of parts) {
            if (cur == null) return path;
            cur = cur[p];
        }
        return cur ?? path;
    };

    return (
        <LanguageContext.Provider value={{ lang, setLang, t }}>
            {children}
        </LanguageContext.Provider>
    );
}

export const useT = () => useContext(LanguageContext);
